import { Capacitor } from '@capacitor/core'
import {
    getUserOrgPref,
    setUserOrgPref,
    setUserPref,
    spLogTags,
} from '@eventr/common'
import {
    ColoursObj,
    Icon,
    MobileConfirmDialog,
    TDialogStore,
    Typography,
    useDialogStore,
} from '@eventr/common-storybook'
import { Box, Switch } from '@mui/material'
import { useEffect } from 'react'
import { isAndroid, isIOS } from 'react-device-detect'
import { useShallow } from 'zustand/react/shallow'
import { useDeviceInfo } from '../../../feat/appState/deviceInfo'
import {
    deleteCalendar,
    isCalendarEnabled,
} from '../../../feat/calendar/calendarProvider'
import { EventrCapacitorCalendar } from '../../../feat/push/plugin'
import i18n from '../../../i18n/i18next'
import { PermissionsPlugin } from '../../../plugins/permissions/PermissionsPlugin'
import { useBoundStore } from '../../../state/bound.store'
import {
    calErrorMessage,
    calIdGlobal,
    calPerm,
    syncing,
} from '../../../state/calendarState/calendarSignal'
import { defaultAddSnack } from '../../../state/overlayState/overlayContext'

export const SyncCalendarRowSummary = () => {
    const { selectedOrg, selectedItinID } = useBoundStore(
        useShallow((s) => {
            return {
                selectedOrg: s.selectedOrg,
                selectedItinID: s.selectedItinID,
            }
        })
    )

    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )

    const itinId = `itinId: ${selectedItinID.toString()} -`

    const checkInitialPermissions = async () => {
        if (Capacitor.isNativePlatform()) {
            try {
                let permStatus =
                    await EventrCapacitorCalendar.checkAllPermissions()
                const hasPermission =
                    Object.values(permStatus).includes('granted')
                if (hasPermission) {
                    spLogTags('Calendar permissions has been enabled', [
                        'CALENDAR',
                    ])
                    calPerm.value = true
                    return true
                }
                if (!hasPermission) {
                    spLogTags('Calendar permissions has not been enabled', [
                        'CALENDAR',
                    ])
                    calPerm.value = false
                    syncing.value = false
                    return false
                }
            } catch (error) {
                console.error('Error checking calendar permissions:', error)
                calPerm.value = false
                syncing.value = false
                return false
            }
        }
    }

    useEffect(() => {
        checkInitialPermissions()
        if (selectedOrg) {
            const activeCalendars = getUserOrgPref(
                'homepage',
                'calendarSync',
                selectedOrg?.name ?? 'Unknown Organization'
            )
            if (activeCalendars) {
                const findCurrentCal = activeCalendars?.find((element: any) =>
                    element.includes(itinId)
                )
                if (findCurrentCal) {
                    spLogTags(`Calendar has been found for ${findCurrentCal}`, [
                        'CALENDAR',
                    ])
                    syncing.value = true
                } else {
                    syncing.value = false
                }
            } else {
                syncing.value = false
            }
        }
    }, [selectedOrg, itinId])

    if (!isCalendarEnabled(useDeviceInfo.getState().version.buildVersion)) {
        return null
    }

    const onChange = async (reminder?: boolean) => {
        spLogTags('Calendar has been toggled', ['CALENDAR'])
        const wasSyncing = syncing.value
        const activeCalendars =
            getUserOrgPref('homepage', 'calendarSync', selectedOrg?.name ?? '') || []

        if (!wasSyncing) {
            try {
                await getCalendarSyncPerm()
                if (!calPerm.value) {
                    defaultAddSnack(i18n.t('snack.calendar.warning'), 'error')
                    return
                }
                await useBoundStore
                    .getState()
                    .syncCalendar(
                        selectedItinID,
                        activeCalendars,
                        false,
                        reminder
                    )

                const allCalendars = (
                    await EventrCapacitorCalendar.listCalendars()
                ).result

                const hasCal = allCalendars.some(
                    (event) =>
                        event.id === calIdGlobal.value.id &&
                        event.title === calIdGlobal.value.title
                )
                if (calPerm.value && syncing.value && hasCal) {
                    defaultAddSnack(i18n.t('snack.calendar.synced'), 'success')
                    spLogTags(
                        `Successfully created a calendar \nCurrent Org: ${selectedOrg?.name ?? 'Unknown Organization'}`,
                        ['CALENDAR']
                    )
                } else if (!hasCal) {
                    syncing.value = false
                    if (calErrorMessage.value) {
                        defaultAddSnack(
                            calErrorMessage.value !== ''
                                ? i18n.t(
                                    `snack.calendar.calendarErrors.${isIOS ? 'ios' : 'android'
                                    }.${calErrorMessage.value}`
                                )
                                : i18n.t('snack.calendar.unsuccessfulSync'),
                            'error'
                        )
                        calErrorMessage.value = ''
                    } else {
                        defaultAddSnack(
                            i18n.t('snack.calendar.unsuccessfulSync'),
                            'error'
                        )
                    }
                    spLogTags(
                        `Failed create a calendar \nCurrent Org: ${selectedOrg?.name ?? 'Unknown Organization'}`,
                        ['CALENDAR']
                    )
                }
            } catch (error) {
                syncing.value = false
                console.error('Error during permission request:', error)
                defaultAddSnack(i18n.t('snack.calendar.warning'), 'error')
            }
        } else {
            const findCurrentCal = activeCalendars.filter((element: string | string[]) =>
                element.includes(itinId)
            )
            if (findCurrentCal) {
                if (calPerm.value) {
                    for (const cal of findCurrentCal) {
                        const calId = cal.split(' - ')
                        try {
                            await deleteCalendar(calId[1], findCurrentCal)
                        } catch (e) {
                            spLogTags(
                                `Unable to delete calendar : ${calId}\nCurrent Org: ${selectedOrg?.name ?? 'Unknown Organization'}`,
                                ['CALENDAR']
                            )
                        }
                    }
                    const appendedCal = activeCalendars.filter(
                        (element: any) => !element.includes(itinId)
                    )
                    spLogTags(
                        `Stored Calendar list: ${appendedCal}\nCurrent Org: ${selectedOrg?.name ?? 'Unknown Organization'}`,
                        ['CALENDAR']
                    )
                    setUserOrgPref(
                        'homepage',
                        'calendarSync',
                        appendedCal,
                        selectedOrg?.name ?? 'Uknown Organization'
                    )
                }
                const allCalendars = (
                    await EventrCapacitorCalendar.listCalendars()
                ).result
                if (!allCalendars.includes(calIdGlobal.value)) {
                    spLogTags(
                        `Successfully able to delete calendar : ${calIdGlobal.value}\nCurrent Org: ${selectedOrg?.name}`,
                        ['CALENDAR']
                    )
                    defaultAddSnack(i18n.t('snack.calendar.unsync'), 'success')
                }
                syncing.value = false
            }
        }
    }

    const howToEnable = isAndroid
        ? i18n.t('info-dialog.calendar.infoAndroid')
        : i18n.t('info-dialog.calendar.infoIos')

    const toolbar = (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto',
                gap: '8px',
            }}
        >
            <Icon name="Sync" iconColour={ColoursObj.coreBrights.secondary} />
            <Typography>{i18n.t('itin-picker.sync-calendar')}</Typography>
            <Box display={'grid'} gridTemplateColumns={'auto auto'}>
                {
                    <div style={{ paddingRight: '10px' }}>
                        <Icon
                            name="Info"
                            iconColour={ColoursObj.core.secondary}
                            onClick={() => {
                                replaceDialog({
                                    node: (
                                        <MobileConfirmDialog
                                            text={howToEnable}
                                            header={i18n.t(
                                                'info-dialog.calendar.headerInfo'
                                            )}
                                            onCancel={() => {
                                                clearDialog()
                                            }}
                                            cancelText={i18n.t(
                                                'info-dialog.okay'
                                            )}
                                            confirmText=""
                                        />
                                    ),
                                    backdropClosable: false,
                                })
                            }}
                        />
                    </div>
                }
                <Switch
                    id="calendarSwitch"
                    sx={{
                        padding: '0px',
                        '&.MuiSwitch-root ': {
                            width: 'fit-content',
                            height: '24px',
                        },
                        '&.MuiSwitch-root .MuiButtonBase-root ': {
                            padding: '4px 2px',
                        },
                    }}
                    checked={calPerm.value ? syncing.value : false}
                    color="secondary"
                    onClick={() => {
                        syncing.value
                            ? onChange()
                            : replaceDialog({
                                node: (
                                    <MobileConfirmDialog
                                        header={i18n.t(
                                            'info-dialog.calendar.header'
                                        )}
                                        text={i18n.t(
                                            'info-dialog.calendar.toSync'
                                        )}
                                        onCancel={() => {
                                            replaceDialog({
                                                node: (
                                                    <MobileConfirmDialog
                                                        header={i18n.t(
                                                            'info-dialog.calendar.header'
                                                        )}
                                                        text={i18n.t(
                                                            'info-dialog.calendar.toAddReminders'
                                                        )}
                                                        onCancel={() => {
                                                            setUserPref(
                                                                'account',
                                                                'calendarReminderEnabled',
                                                                true
                                                            )
                                                            onChange(true)
                                                            clearDialog()
                                                        }}
                                                        onConfirm={() => {
                                                            setUserPref(
                                                                'account',
                                                                'calendarReminderEnabled',
                                                                false
                                                            )
                                                            onChange(false)
                                                            clearDialog()
                                                        }}
                                                        cancelText={i18n.t(
                                                            'info-dialog.yes'
                                                        )}
                                                        confirmText={i18n.t(
                                                            'info-dialog.no'
                                                        )}
                                                    />
                                                ),
                                                backdropClosable: false,
                                            })
                                        }}
                                        onConfirm={() => {
                                            clearDialog()
                                        }}
                                        cancelText={i18n.t('info-dialog.yes')}
                                        confirmText={i18n.t('info-dialog.no')}
                                    />
                                ),
                                backdropClosable: false,
                            })
                    }}
                />
            </Box>
        </div>
    )

    return toolbar
}

export async function getCalendarSyncPerm() {
    try {
        if (!Capacitor.isNativePlatform()) return
        const platform = Capacitor.getPlatform()
        let permStatus = await EventrCapacitorCalendar.checkAllPermissions()
        const hasPermission = Object.values(permStatus).includes('granted')

        if (hasPermission) {
            calPerm.value = true
            syncing.value = true
            return
        }

        if (platform === 'android') {
            const status = await PermissionsPlugin.requestCalendarPermissions()
            if (status?.['calendar'] === 'granted') {
                calPerm.value = true
                syncing.value = true
            } else {
                calPerm.value = false
            }
        } else if (platform === 'ios') {
            const iosPermStatus =
                await EventrCapacitorCalendar.requestAllPermissions()
            if (Object.values(iosPermStatus).includes('granted')) {
                calPerm.value = true
                syncing.value = true
            } else {
                calPerm.value = false
            }
        }
    } catch (error) {
        spLogTags(`Could not get calendar permissions`, ['CALENDAR'])
        console.error('Error checking calendar permissions:', error)
    }
}
